<template>
  <RevButtonBase
    :aria-disabled="disabled"
    class="focus-visible-outline-default-hi rounded-sm relative flex size-full flex-row items-center border py-12 pl-8 pr-16 no-underline"
    :class="{
      'bg-static-default-low-disabled border-action-default-low-disabled':
        disabled,
      'bg-surface-brand-hi border-action-default-low-pressed hover:bg-surface-default-mid hover:border-action-default-low':
        selected,
      'bg-static-default-low border-action-default-low hover:bg-surface-default-mid':
        !disabled && !selected,
    }"
    :disabled="disabled"
    :to="to"
    @click="emit('click')"
  >
    <RevSpinner
      v-if="loading"
      aria-hidden
      class="text-action-default-hi absolute left-0 top-1/2 -mt-12 w-full"
    />

    <div class="mr-16 shrink-0" :class="{ invisible: loading }">
      <PickerRound :index="index" :selected="selected" />
    </div>
    <div class="flex grow flex-col" :class="{ invisible: loading }">
      <div class="flex grow flex-row items-baseline text-left">
        <span
          class="grow"
          :class="{
            'text-action-default-hi body-1-bold': selected,
            'text-action-default-hi-disabled body-1': disabled,
            'text-action-default-hi body-1': !disabled && !selected,
          }"
        >
          {{ label }}
        </span>
        <span
          class="body-2 ml-8 shrink-0"
          :class="{
            'text-action-default-low': !disabled,
            'text-action-default-low-disabled': disabled,
            invisible: loading,
          }"
        >
          {{ price }}
        </span>
      </div>

      <div
        v-if="subLabel"
        class="caption text-left"
        :class="{
          'text-action-default-low': !disabled,
          'text-action-default-low-disabled': disabled,
        }"
      >
        {{ subLabel }}
      </div>
    </div>

    <RevTag
      v-if="tagLabel && tagVariant"
      class="absolute -top-8 right-16"
      :class="{ invisible: loading }"
      :label="tagLabel"
      :variant="tagVariant"
    />
  </RevButtonBase>
</template>

<script lang="ts" setup>
/**
 * This component is used in place of the RevPicker provided by the design system. We used to override some of its styles to fit our needs,
 * but several issues prevented us to continue this way:
 * - The ability to position the tag differently (every picker)
 * - The ability to add a sub label (new battery, sim)
 * This will of course need to be discussed after the migration to FSM to see if those changes belongs to the existing component (as a variant?)
 * or should live in our scope.
 */
import { computed } from 'vue'

import { type LinkInternal } from '@backmarket/http-api'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag, type Variants as TagVariants } from '@ds/components/Tag'

import PickerRound from '../PickerRound/PickerRound.vue'

const props = withDefaults(
  defineProps<{
    to?: LinkInternal
    index: number
    label: string
    subLabel?: string
    price: string
    selected?: boolean
    disabled?: boolean
    loading?: boolean
    tag: string | { label: string; variant?: TagVariants }
  }>(),
  {
    to: undefined,
    subLabel: '',
    selected: false,
    disabled: false,
    loading: false,
  },
)

const emit = defineEmits(['click'])

const tagLabel = computed(() => {
  if (!props.tag) {
    return ''
  }

  return typeof props.tag === 'string' ? props.tag : props.tag?.label
})

const tagVariant = computed(() => {
  return typeof props.tag === 'string'
    ? 'primary'
    : props.tag?.variant ?? 'primary'
})
</script>
